import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Eye, Download, Edit, ChevronDown } from 'lucide-react';

const statusColors = {
    'Draft': { dot: 'bg-slate-400', text: 'text-slate-600', bg: 'bg-slate-50', border: 'border-slate-300', badge: 'status-badge-draft' },
    'Sent': { dot: 'bg-blue-500', text: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-300', badge: 'status-badge-sent' },
    'Completed': { dot: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-300', badge: 'status-badge-completed' },
    'Dropped': { dot: 'bg-rose-500', text: 'text-rose-700', bg: 'bg-rose-50', border: 'border-rose-300', badge: 'status-badge-dropped' },
};

const ALL_STATUSES = ['Draft', 'Sent', 'Completed', 'Dropped'];

const StatusBadge = ({ quote, onStatusChange }) => {
    const [open, setOpen] = useState(false);
    const [dropPos, setDropPos] = useState({ top: 0, left: 0 });
    const ref = useRef(null);
    const triggerRef = useRef(null);

    const currentStatus = quote.status || 'Draft';
    const style = statusColors[currentStatus] || statusColors['Draft'];
    const options = ALL_STATUSES;

    // Close on outside click
    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleTriggerClick = () => {
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            setDropPos({ top: rect.bottom + 6, left: rect.left });
        }
        setOpen(o => !o);
    };

    return (
        <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
            {/* Trigger */}
            <div
                ref={triggerRef}
                onClick={handleTriggerClick}
                style={{ cursor: 'pointer', userSelect: 'none' }}
                className={`status-badge ${style.badge}`}
            >
                <div className={`status-indicator ${style.dot} shrink-0`} />
                <span style={{ fontWeight: 700 }}>{currentStatus}</span>
                <ChevronDown
                    size={13}
                    style={{ marginLeft: 4, transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
                />
            </div>

            {/* Dropdown panel — fixed so it escapes table overflow clipping */}
            {open && (
                <div style={{
                    position: 'fixed',
                    top: dropPos.top,
                    left: dropPos.left,
                    zIndex: 9999,
                    background: '#fff',
                    border: '1.5px solid #e2e8f0',
                    borderRadius: '12px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    minWidth: '150px',
                    overflow: 'hidden',
                    padding: '4px',
                }}>
                    {options.map(statusKey => {
                        const s = statusColors[statusKey];
                        const isCurrent = statusKey === currentStatus;
                        return (
                            <div
                                key={statusKey}
                                onClick={() => {
                                    if (!isCurrent) {
                                        onStatusChange(quote._id || quote.id, statusKey);
                                    }
                                    setOpen(false);
                                }}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '8px 12px',
                                    borderRadius: '8px',
                                    cursor: isCurrent ? 'default' : 'pointer',
                                    fontWeight: isCurrent ? 700 : 500,
                                    fontSize: '13px',
                                    transition: 'background 0.15s',
                                }}
                                className={`${isCurrent ? s.bg : 'hover:bg-slate-50'} ${s.text}`}
                            >
                                <span style={{
                                    width: 8, height: 8, borderRadius: '50%', display: 'inline-block', flexShrink: 0
                                }} className={s.dot} />
                                {statusKey}
                                {isCurrent && (
                                    <span style={{ marginLeft: 'auto', fontSize: '10px', opacity: 0.6 }}>current</span>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const QuotationPreview = () => {
    const [quotes, setQuotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchQuotes = async () => {
            try {
                const res = await api.get('/quotes');
                setQuotes(res.data.data);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchQuotes();
    }, []);

    const handleView = (id) => {
        navigate(`/quotes/${id}`);
    };

    const handleEdit = (id) => {
        navigate(`/builder?id=${id}`);
    };

    const handleDownload = (id) => {
        console.log(`Downloading quote ${id}`);
    };

    const handleStatusChange = async (quoteId, newStatus) => {
        try {
            const body = { status: newStatus };
            await api.patch(`/quotes/${quoteId}/status`, body);
            setQuotes(quotes.map(q => (q.id === quoteId || q._id === quoteId) ? { ...q, status: newStatus } : q));
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <div className="p-4 md:p-10 max-w-[1400px] mx-auto">
            <h1 className="text-2xl md:text-[2.2rem] font-[900] text-primary mb-10 tracking-tight">Quotation Preview</h1>

            <div className="bg-white rounded-2xl p-1 md:p-1 border-2 border-slate-200 overflow-hidden shadow-sm">
                <table className="data-table !border-none !rounded-none">
                    <thead>
                        <tr>
                            <th>Quote Number</th>
                            <th>Customer Name</th>
                            <th>Assigned Executive</th>
                            <th>Total Amount</th>
                            <th>Creation Date</th>
                            <th>Current Status</th>
                            <th className="text-center !border-r-0">Available Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="7" className="p-12 text-center font-bold text-slate-400 italic">Fetching quotation records...</td></tr>
                        ) : quotes.length === 0 ? (
                            <tr><td colSpan="7" className="p-12 text-center font-bold text-slate-400 italic">No quotes found in database</td></tr>
                        ) : (
                            quotes.map(quote => (
                                <tr key={quote._id || quote.id}>
                                    <td data-label="Quote #"><span className="font-[800] text-primary">{quote.quote_number}</span></td>
                                    <td data-label="Customer" className="font-medium text-slate-700">{quote.customer_id?.company_name || quote.customer_name}</td>
                                    <td data-label="Executive">
                                        <div className="flex flex-col">
                                            <span className="font-bold text-slate-800">{quote.executive_id?.name}</span>
                                            <span className="text-[10px] text-slate-400 uppercase font-bold tracking-tighter">{quote.executive_id?.designation}</span>
                                        </div>
                                    </td>
                                    <td className="font-[900] text-slate-900" data-label="Amount">
                                        AED {Number(quote.total_amount || 0).toFixed(2)}
                                    </td>
                                    <td className="text-slate-500 font-medium" data-label="Date">
                                        {new Date(quote.date || quote.created_at).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })}
                                    </td>
                                    <td data-label="Status">
                                        <StatusBadge quote={quote} onStatusChange={handleStatusChange} />
                                    </td>
                                    <td className="text-center !border-r-0" data-label="Actions">
                                        <div className="flex justify-center gap-2">
                                            <button
                                                onClick={() => handleEdit(quote._id || quote.id)}
                                                className="px-3 py-1.5 text-xs font-bold rounded-lg text-accent border-2 border-accent hover:bg-accent hover:text-white transition-all shadow-sm"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => handleView(quote._id || quote.id)}
                                                className="px-3 py-1.5 text-xs font-bold rounded-lg text-primary border-2 border-primary hover:bg-primary hover:text-white transition-all shadow-sm"
                                            >
                                                View
                                            </button>
                                            <button
                                                onClick={() => handleDownload(quote._id || quote.id)}
                                                className="px-3 py-1.5 text-xs font-bold rounded-lg text-slate-700 border-2 border-slate-300 hover:bg-slate-800 hover:text-white hover:border-slate-800 transition-all shadow-sm"
                                            >
                                                Download
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default QuotationPreview;
